import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, X, Settings, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Onboarding "Finish setup" checklist for platform owners. Shows which critical
 * integrations (PayMongo, SMS/OTP, Email) still need configuring and links to
 * the Owner Panel's Configuration tab. Auto-hides once everything is set up.
 */
const SetupChecklist = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('gj_setup_dismissed') === '1'
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/setup-status`);
        if (!cancelled) setStatus(r.data);
      } catch (e) {
        /* non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status || status.complete || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem('gj_setup_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-5 shadow-sm"
      data-testid="setup-checklist"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-orange-100 text-orange-600 shrink-0">
            <Settings className="w-5 h-5" />
          </span>
          <div>
            <h3 className="text-base font-bold text-gray-900">Finish setting up your platform</h3>
            <p className="text-sm text-gray-500">
              {status.done_count} of {status.total} complete — get fully live in a few steps.
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600 p-1"
          data-testid="setup-checklist-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-2">
        {status.items.map((item) => (
          <button
            key={item.key}
            onClick={() => !item.done && navigate('/owner?tab=config')}
            disabled={item.done}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
              item.done
                ? 'bg-green-50 cursor-default'
                : 'bg-white hover:bg-orange-50 border border-gray-100'
            }`}
            data-testid={`setup-item-${item.key}`}
          >
            {item.done ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-gray-300 shrink-0" />
            )}
            <span className="flex-1 min-w-0">
              <span
                className={`block text-sm font-semibold ${
                  item.done ? 'text-gray-500 line-through' : 'text-gray-900'
                }`}
              >
                {item.label}
              </span>
              {!item.done && (
                <span className="block text-xs text-gray-500">{item.description}</span>
              )}
            </span>
            {!item.done && <ArrowRight className="w-4 h-4 text-orange-500 shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SetupChecklist;
